import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, FormGroup, Input, Label, Row } from 'reactstrap';
import Loader from './common/Loader.jsx';
import ResponseErrorMessage from './common/ResponseErrorMessage.jsx';
import checkFeatureFlag from './common/checkFeatureFlag.js';
import useDebouncedValue from './standards/useDebouncedValue.js';
import StandardBrowser from './standards/StandardBrowser.jsx';
import StandardContent from './standards/StandardContent.jsx';
import { fetchTp3, standardHandle } from './standards/standardsApi.js';

export const featureFlagKey = 'teacher-standards-search';

/**
 * "Find content by standard" for teachers and administrators (DH-361, epic TP-1316)
 *
 * Search or browse a state's standards (Utah, Texas) and list the Shmoop test-prep content aligned
 * to one. Data comes from test-prep-iii on the same origin (TP-1321). Dark by default: it renders
 * only when the teacher-standards-search feature flag is on for the viewer, and renders nothing
 * while that check is pending, when it fails, or when TP3 answers 403.
 *
 * The state defaults to the first one offered; nothing reliable says which state a teacher's
 * school is in yet.
 */
const FindContentByStandard = props => {
	const { user } = props;
	const isTeacherOrAdmin = user.type === 'teacher' || user.type === 'admin';
	const [flagEnabled, setFlagEnabled] = useState(null);
	const [forbidden, setForbidden] = useState(false);
	const [frameworks, setFrameworks] = useState(null);
	const [frameworksError, setFrameworksError] = useState(null);
	const [stateLabel, setStateLabel] = useState('');
	const [frameworkUuid, setFrameworkUuid] = useState('');
	const [search, setSearch] = useState('');
	const [results, setResults] = useState(null);
	const [searching, setSearching] = useState(false);
	const [searchError, setSearchError] = useState(null);
	const [selectedUuid, setSelectedUuid] = useState(null);
	const debouncedSearch = useDebouncedValue(search.trim(), 300);
	const onForbidden = () => setForbidden(true);

	/**
	 * Is the feature flag on for this viewer? Stays null (render nothing) until it answers
	 */
	useEffect(() => {
		if (isTeacherOrAdmin && typeof user.emailAddress !== 'undefined') {
			checkFeatureFlag(featureFlagKey, user.emailAddress, setFlagEnabled, () => {});
		}
	}, [user.emailAddress, isTeacherOrAdmin]);

	/**
	 * Load the frameworks once the flag is on; pick the first state and its first framework
	 */
	useEffect(() => {
		if (flagEnabled !== true) {
			return () => {};
		}
		const abortController = new AbortController();

		fetchTp3('GetTestPrepStandardFrameworks', {}, abortController.signal)
			.then(payload => {
				setFrameworks(payload.frameworks);
				if (payload.frameworks.length > 0) {
					setStateLabel(payload.frameworks[0].authorityLabel);
					setFrameworkUuid(payload.frameworks[0].uuid);
				}
			})
			.catch(e => {
				if (abortController.signal.aborted) {
					return;
				}
				if (e.status === 403) {
					setForbidden(true);
				} else {
					setFrameworksError(e);
				}
			});

		return () => abortController.abort();
	}, [flagEnabled]);

	const states = frameworks === null ? [] : [...new Set(frameworks.map(framework => framework.authorityLabel))];
	const stateFrameworks = frameworks === null ? [] :
		frameworks.filter(framework => framework.authorityLabel === stateLabel);
	const authority = stateFrameworks.length > 0 ? stateFrameworks[0].authority : '';
	const stateOptions = states.map(label => <option key={label} value={label}>{ label }</option>);

	/**
	 * Search the selected state's standards as the teacher types (2+ characters)
	 */
	useEffect(() => {
		if (debouncedSearch.length < 2 || authority === '') {
			setResults(null);
			return () => {};
		}
		const abortController = new AbortController();

		setSearching(true);
		setSearchError(null);
		fetchTp3('GetTestPrepStandards', { search: debouncedSearch, authority: authority }, abortController.signal)
			.then(payload => setResults(payload))
			.catch(e => {
				if (abortController.signal.aborted) {
					return;
				}
				if (e.status === 403) {
					setForbidden(true);
				} else {
					setSearchError(e);
				}
			})
			.finally(() => !abortController.signal.aborted && setSearching(false));

		return () => abortController.abort();
	}, [debouncedSearch, authority]);

	/**
	 * Switching state resets the framework to that state's first one
	 *
	 * @param label
	 */
	const changeState = label => {
		const first = frameworks.find(framework => framework.authorityLabel === label);

		setStateLabel(label);
		setFrameworkUuid(first ? first.uuid : '');
		setSelectedUuid(null);
	};

	/**
	 * Search hits, or the browse tree when there is no search
	 *
	 * @returns {JSX.Element}
	 */
	const getFinder = () => {
		if (debouncedSearch.length >= 2) {
			if (searching || results === null) {
				return <Loader />;
			}
			if (searchError !== null) {
				return <ResponseErrorMessage error={searchError} />;
			}

			return (
				<>
					<p className={'standard-result-count'} aria-live={'polite'}>
						{ results.pagination.totalFiltered } { results.pagination.totalFiltered === 1 ?
							'standard matches' : 'standards match' } “{ debouncedSearch }”
					</p>
					<ul className={'standard-list standard-results'}>
						{
							results.items.map(hit =>
								<li key={hit.uuid}>
									<button type={'button'}
									        className={'btn btn-link standard-leaf' +
										        (hit.uuid === selectedUuid ? ' active' : '') +
										        (hit.alignedLeafCount === 0 ? ' text-muted' : '')}
									        aria-pressed={hit.uuid === selectedUuid}
									        onClick={() => setSelectedUuid(hit.uuid)}>
										{ standardHandle(hit) !== '' && <strong>{ standardHandle(hit) } </strong> }
										{ hit.statement }
										<small className={'d-block standard-coverage'}>{ hit.frameworkTitle }</small>
									</button>
								</li>
							)
						}
					</ul>
				</>
			);
		}

		return frameworkUuid === '' ? <></> :
			<StandardBrowser frameworkUuid={frameworkUuid} selectedUuid={selectedUuid}
			                 onSelect={setSelectedUuid} onForbidden={onForbidden} />;
	};

	if (!isTeacherOrAdmin || flagEnabled !== true || forbidden) {
		return null;
	}

	return (
		<div className={'find-by-standard row'}>
			<div className={'find-by-standard-wrapper col-11 col-offset-1'}>
				<h2 className={'title-line artz'}>Find content by standard</h2>
				{ frameworksError !== null && <ResponseErrorMessage error={frameworksError} /> }
				{ frameworks === null && frameworksError === null && <Loader /> }
				{
					frameworks !== null &&
						<>
							<Row className={'find-by-standard-controls'}>
								<Col md={3}>
									<FormGroup>
										<Label for={'standards-state'}>State</Label>
										<Input type={'select'} id={'standards-state'} value={stateLabel}
										       onChange={e => changeState(e.target.value)}>
											{ stateOptions }
										</Input>
									</FormGroup>
								</Col>
								<Col md={4}>
									<FormGroup>
										<Label for={'standards-framework'}>Course</Label>
										<Input type={'select'} id={'standards-framework'} value={frameworkUuid}
										       onChange={e => {
											       setFrameworkUuid(e.target.value);
											       setSelectedUuid(null);
										       }}>
											{
												stateFrameworks.map(framework =>
													<option key={framework.uuid} value={framework.uuid}>
														{ framework.title }
													</option>
												)
											}
										</Input>
									</FormGroup>
								</Col>
								<Col md={5}>
									<FormGroup>
										<Label for={'standards-search'}>Search { stateLabel } standards</Label>
										<Input type={'search'} id={'standards-search'} value={search}
										       placeholder={'Code or words, e.g. two-way table'}
										       onChange={e => setSearch(e.target.value)} />
									</FormGroup>
								</Col>
							</Row>
							<Row>
								<Col lg={5} className={'find-by-standard-finder'}>{ getFinder() }</Col>
								<Col lg={7} className={'find-by-standard-content'}>
									{
										selectedUuid === null ?
											<p className={'standard-hint'}>
												Pick a standard to see the Shmoop content aligned to it.
											</p> :
											<StandardContent standardUuid={selectedUuid} onForbidden={onForbidden} />
									}
								</Col>
							</Row>
						</>
				}
			</div>
		</div>
	);
};

FindContentByStandard.propTypes = {
	user: PropTypes.object.isRequired
};

export default FindContentByStandard;
